import { useBoardCamera } from "./use-board-camera";
import { cardLabel } from "../cards/card-art";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent,
} from "react";
import type { DisplayProjection, PlayerProjection } from "@saboteur/contracts";
import { CardFace, GoalCard } from "../cards/card-face";
import "./game-board.css";

type Rotation = 0 | 180;
type Position = { x: number; y: number };
export interface SelectedPathCard {
  readonly cardId: string;
  readonly faceId: string;
}
export interface PathPlacementSelection extends Position {
  readonly cardId: string;
  readonly rotation: Rotation;
}
export interface GameBoardProps {
  readonly board: DisplayProjection["board"];
  readonly participants: DisplayProjection["participants"];
  readonly activeParticipantId: string | null;
  readonly recentActions: DisplayProjection["recentActions"];
  readonly selectedPathCard?: SelectedPathCard | null;
  readonly legalPathPlacements?: PlayerProjection["self"]["legalCommands"]["pathPlacements"];
  readonly initialRotation?: Rotation;
  readonly onRotationChange?: (rotation: Rotation) => void;
  readonly onPlacePath?: (placement: PathPlacementSelection) => void;
  readonly preview?: PathPlacementSelection | null;
  readonly removablePositions?: readonly Position[];
  readonly onRemovePath?: (position: Position) => void;
  readonly inspectableGoalIds?: readonly string[];
  readonly onInspectGoal?: (goalId: string) => void;
  readonly removalPreview?: Position | null;
  readonly disabled?: boolean;
  readonly readOnly?: boolean;
  readonly className?: string;
}
const W = 100,
  H = 140;
const positionStyle = ({ x, y }: Position): CSSProperties => ({ left: x * W, top: y * H });

export function GameBoard({
  board,
  selectedPathCard = null,
  legalPathPlacements = [],
  initialRotation = 0,
  onRotationChange,
  onPlacePath,
  preview,
  removablePositions = [],
  onRemovePath,
  inspectableGoalIds = [],
  onInspectGoal,
  removalPreview,
  disabled = false,
  readOnly = false,
  className = "",
}: GameBoardProps) {
  // A reconnect renders the existing board quietly; only new cards get an impact.
  const [initialCards] = useState(() => new Set(board.paths.map((card) => card.cardId)));
  const [rotation, setRotation] = useState<Rotation>(initialRotation);
  const viewport = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const { canvas, grid, setCamera } = useBoardCamera(viewport);
  const gestureOrigin = useRef({ x: 0, y: 0 });
  const fitted = useRef(false);
  const pointers = useRef(new Map<number, Position>());
  const moved = useRef(false);
  const start = useRef<Position | null>(null);
  useEffect(() => setRotation(initialRotation), [initialRotation, selectedPathCard?.cardId]);
  useEffect(() => {
    const element = viewport.current;
    if (!element) return;
    const resize = () => setSize({ width: element.clientWidth, height: element.clientHeight });
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const placements = useMemo(
    () =>
      selectedPathCard
        ? legalPathPlacements.filter((p) => p.cardId === selectedPathCard.cardId)
        : [],
    [legalPathPlacements, selectedPathCard],
  );
  const coordinates = [...board.paths, ...board.goals];
  const minX = Math.min(0, ...coordinates.map((p) => p.x)) * W - 45;
  const minY = Math.min(0, ...coordinates.map((p) => p.y)) * H - 45;
  const width = (Math.max(0, ...coordinates.map((p) => p.x)) + 1) * W + 45 - minX;
  const height = (Math.max(0, ...coordinates.map((p) => p.y)) + 1) * H + 45 - minY;
  const fit = useCallback(() => {
    if (!size.width || !size.height) return;
    const availableHeight = Math.max(1, size.height);
    const zoom = Math.min(size.width / width, availableHeight / height, 1.6);
    setCamera({
      x: (size.width - width * zoom) / 2 - minX * zoom,
      y: (availableHeight - height * zoom) / 2 - minY * zoom,
      zoom,
    });
    fitted.current = true;
  }, [size.width, size.height, width, height, minX, minY, setCamera]);
  useEffect(() => {
    if (readOnly || !fitted.current) fit();
  }, [fit, readOnly]);
  // Card controls change the available height. Preserve the player's camera through
  // those changes; only a new screen width (such as orientation) should refit it.
  useEffect(() => {
    fitted.current = false;
    fit();
  }, [size.width]); // eslint-disable-line react-hooks/exhaustive-deps
  function zoomBy(factor: number, anchor = { x: size.width / 2, y: size.height / 2 }) {
    setCamera((c) => {
      const zoom = Math.max(0.15, Math.min(2.5, c.zoom * factor));
      const ratio = zoom / c.zoom;
      return {
        zoom,
        x: anchor.x - (anchor.x - c.x) * ratio,
        y: anchor.y - (anchor.y - c.y) * ratio,
      };
    });
  }
  function down(e: PointerEvent<HTMLDivElement>) {
    if (e.button !== 0) return;
    if (!pointers.current.size) {
      const rect = e.currentTarget.getBoundingClientRect();
      gestureOrigin.current = { x: rect.left, y: rect.top };
      moved.current = false;
      start.current = { x: e.clientX, y: e.clientY };
    }
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (e.nativeEvent.isTrusted && !(e.target as Element).closest("button"))
      e.currentTarget.setPointerCapture(e.pointerId);
  }
  function move(e: PointerEvent<HTMLDivElement>) {
    const before = pointers.current.get(e.pointerId);
    if (!before) return;
    if (start.current && Math.hypot(e.clientX - start.current.x, e.clientY - start.current.y) > 6)
      moved.current = true;
    if (
      e.nativeEvent.isTrusted &&
      moved.current &&
      e.currentTarget.hasPointerCapture?.(e.pointerId) === false
    )
      e.currentTarget.setPointerCapture(e.pointerId);
    const other = [...pointers.current.entries()].find(([id]) => id !== e.pointerId)?.[1];
    if (other) {
      moved.current = true;
      const oldDistance = Math.hypot(before.x - other.x, before.y - other.y);
      const distance = Math.hypot(e.clientX - other.x, e.clientY - other.y);
      if (oldDistance > 0) {
        const origin = gestureOrigin.current;
        const previous = {
          x: (other.x + before.x) / 2 - origin.x,
          y: (other.y + before.y) / 2 - origin.y,
        };
        const next = {
          x: (other.x + e.clientX) / 2 - origin.x,
          y: (other.y + e.clientY) / 2 - origin.y,
        };
        setCamera((c) => {
          const zoom = Math.max(0.15, Math.min(2.5, (c.zoom * distance) / oldDistance));
          return {
            zoom,
            x: next.x - ((previous.x - c.x) * zoom) / c.zoom,
            y: next.y - ((previous.y - c.y) * zoom) / c.zoom,
          };
        });
      }
    } else if (moved.current)
      setCamera((c) => ({ ...c, x: c.x + e.clientX - before.x, y: c.y + e.clientY - before.y }));
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
  }
  const end = (e: PointerEvent<HTMLDivElement>) => {
    pointers.current.delete(e.pointerId);
  };
  function rotate() {
    const next = rotation === 0 ? 180 : 0;
    setRotation(next);
    onRotationChange?.(next);
  }
  return (
    <section
      className={`game-board ${readOnly ? "game-board--tv" : ""} ${className}`}
      aria-label="The mine"
    >
      {!readOnly && (
        <div className="game-board__controls" role="group" aria-label="Board view">
          <button type="button" aria-label="Zoom out" onClick={() => zoomBy(0.8)}>
            −
          </button>
          <button type="button" aria-label="Fit mine" onClick={fit}>
            Fit
          </button>
          <button type="button" aria-label="Zoom in" onClick={() => zoomBy(1.25)}>
            +
          </button>
        </div>
      )}
      <div
        ref={viewport}
        className="game-board__viewport"
        tabIndex={readOnly ? undefined : 0}
        aria-label="Mine viewport. Arrow keys pan; plus and minus zoom; F fits the mine; R rotates a selected tunnel."
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
        onClickCapture={(e) => {
          if (moved.current && e.detail > 0) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          const delta: Record<string, Position> = {
            ArrowLeft: { x: 50, y: 0 },
            ArrowRight: { x: -50, y: 0 },
            ArrowUp: { x: 0, y: 50 },
            ArrowDown: { x: 0, y: -50 },
          };
          const d = delta[e.key];
          if (d) {
            e.preventDefault();
            setCamera((c) => ({ ...c, x: c.x + d.x, y: c.y + d.y }));
          }
          if (e.key === "+" || e.key === "=") zoomBy(1.25);
          if (e.key === "-") zoomBy(0.8);
          if (e.key.toLowerCase() === "f") fit();
          if (e.key.toLowerCase() === "r" && selectedPathCard) rotate();
        }}
        onWheel={(e) => {
          zoomBy(Math.exp(-e.deltaY * 0.002));
        }}
      >
        <div ref={grid} className="mine-grid" />
        <div ref={canvas} className="game-board__canvas">
          {board.paths
            .filter((card) => !board.goals.some((goal) => goal.x === card.x && goal.y === card.y))
            .map((card) => {
              const removable = removablePositions.some((p) => p.x === card.x && p.y === card.y);
              const removing = removalPreview?.x === card.x && removalPreview.y === card.y;
              return (
                <div
                  className={`mine-card ${removing ? "mine-card--removing" : ""}`}
                  data-arriving={!initialCards.has(card.cardId)}
                  style={positionStyle(card)}
                  key={card.cardId}
                >
                  <CardFace faceId={card.faceId} rotation={card.rotation} />
                  {removable ? (
                    <button
                      className="mine-target"
                      type="button"
                      aria-label={`Remove ${cardLabel(card.faceId)} at column ${card.x}, row ${card.y}`}
                      disabled={disabled}
                      onClick={() => onRemovePath?.({ x: card.x, y: card.y })}
                    >
                      ×
                    </button>
                  ) : null}
                </div>
              );
            })}
          {[...board.goals]
            .sort((a, b) => a.y - b.y)
            .map((goal, index) => (
              <div
                className={`mine-card mine-card--goal ${goal.revealed ? "is-revealed" : ""}`}
                key={goal.goalId}
                role="group"
                aria-label={`Destination ${String.fromCharCode(65 + index)}`}
                style={positionStyle(goal)}
              >
                <GoalCard
                  face={goal.revealed ? (goal.face ?? null) : null}
                  rotation={
                    goal.rotation ??
                    board.paths.find((p) => p.x === goal.x && p.y === goal.y)?.rotation ??
                    0
                  }
                />
                {inspectableGoalIds.includes(goal.goalId) ? (
                  <button
                    type="button"
                    className="mine-target"
                    aria-label={`Inspect goal ${String.fromCharCode(65 + index)}`}
                    disabled={disabled}
                    onClick={() => onInspectGoal?.(goal.goalId)}
                  >
                    Inspect
                  </button>
                ) : null}
              </div>
            ))}
          {placements.map((p) => {
            const placementRotation = p.rotations.includes(rotation) ? rotation : p.rotations[0]!;
            const needsRotation = placementRotation !== rotation;
            const selected =
              preview?.x === p.position.x &&
              preview.y === p.position.y &&
              preview.rotation === rotation;
            const nextRotation = rotation === 0 ? 180 : 0;
            const canRotate = selected && p.rotations.includes(nextRotation);
            return (
              <button
                className={`mine-placement ${selected ? "is-preview" : ""} ${needsRotation ? "needs-rotation" : ""}`}
                style={positionStyle(p.position)}
                type="button"
                key={`${p.position.x},${p.position.y}`}
                aria-label={
                  canRotate
                    ? "Rotate previewed tunnel"
                    : `Place selected path at column ${p.position.x}, row ${p.position.y}, rotated ${placementRotation} degrees${needsRotation ? ". Rotates automatically" : ""}`
                }
                data-rotation={placementRotation}
                aria-pressed={selected}
                disabled={disabled}
                onClick={() => {
                  if (!selectedPathCard) return;
                  const next = canRotate ? nextRotation : placementRotation;
                  if (next !== rotation) {
                    setRotation(next);
                    onRotationChange?.(next);
                  }
                  onPlacePath?.({
                    cardId: selectedPathCard.cardId,
                    ...p.position,
                    rotation: next,
                  });
                }}
              >
                {selected && selectedPathCard ? (
                  <>
                    <CardFace faceId={selectedPathCard.faceId} rotation={rotation} />
                    {canRotate ? (
                      <span className="mine-placement__rotate" aria-hidden="true">
                        ↻
                      </span>
                    ) : null}
                  </>
                ) : (
                  <span>{needsRotation ? "↻" : "＋"}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>
      {selectedPathCard ? (
        <p className="game-board__hint" role="status" aria-live="polite">
          {disabled
            ? "Inspect your card while waiting for your turn."
            : placements.length
              ? "Tap a space · ↻ rotates to fit"
              : "This card has no legal positions."}
        </p>
      ) : null}
    </section>
  );
}
